import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Globe, Check, X, Loader2, Lock, Copy, Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Subdomain claim/edit panel.
 *
 * Props:
 *   compact: bool  → tightens layout for use inside onboarding wizard
 *   onClaimed?: (sub) => void  → fires after a successful claim
 */
export default function SubdomainClaim({ compact = false, onClaimed }) {
  const [b, setB] = useState(null);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [check, setCheck] = useState(null); // {available, reason, url}
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef();

  const reload = () =>
    api.get("/tenant/branding").then((r) => {
      setB(r.data);
      setValue(r.data.subdomain || "");
    });
  useEffect(() => { reload(); }, []);

  // Debounced availability check (runs whenever the user types in the form)
  useEffect(() => {
    // Only relevant when the form is actually visible — i.e. no subdomain yet OR user clicked "Change"
    if (b?.subdomain && !editing) return;
    if (!value || value.length < 3) { setCheck(null); return; }
    if (b?.subdomain === value) { setCheck({ available: true, url: b.subdomain_url, current: true }); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setChecking(true);
      try {
        const r = await api.get("/tenant/subdomain/check", { params: { subdomain: value } });
        setCheck(r.data);
      } catch (e) {
        setCheck({ available: false, reason: e?.response?.data?.detail || "Check failed" });
      } finally { setChecking(false); }
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [value, editing, b]);

  const claim = async () => {
    if (!check?.available) return;
    setSaving(true);
    try {
      const r = await api.post("/tenant/subdomain", { subdomain: value });
      const root = b?.root_domain || "axia.app";
      toast.success(`Claimed ${r.data.subdomain}.${root}`);
      setEditing(false);
      onClaimed?.(r.data.subdomain);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not claim");
    } finally { setSaving(false); }
  };

  if (!b) return <div className="text-xs text-slate-400">Loading branding…</div>;

  const lockedUntil = b.subdomain_locked_until ? new Date(b.subdomain_locked_until) : null;
  const showLock = !b.can_change_subdomain && lockedUntil;
  const root = b.root_domain || "axia.app";

  // CLAIMED + not editing: show the brand card
  if (b.subdomain && !editing) {
    return (
      <div className={`axia-card ${compact ? "p-4" : "p-6"} space-y-3`} data-testid="subdomain-claim-card">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-violet-600" />
          <div className="axia-overline">Your branded URL</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <code className="flex-1 min-w-0 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md font-mono text-sm text-slate-900 truncate" data-testid="subdomain-url">
            {b.subdomain_url}
          </code>
          <CopyBtn text={b.subdomain_url} />
          <a
            href={`/preview/${b.subdomain}`}
            target="_blank"
            rel="noreferrer"
            data-testid="subdomain-preview"
          >
            <Button variant="outline" size="sm">
              <ExternalLink className="w-3 h-3 mr-1.5" /> Preview
            </Button>
          </a>
          {b.can_change_subdomain ? (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="subdomain-change">Change</Button>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-slate-500" title={`Locked until ${lockedUntil?.toLocaleDateString()}`}>
              <Lock className="w-3 h-3" /> locked
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500">
          Share this URL with your team — every kudos, public review and webhook callback will use <code className="font-mono">{b.subdomain}.{root}</code>.
        </p>
        {showLock && (
          <p className="text-[11px] text-amber-700 inline-flex items-center gap-1.5"><Lock className="w-3 h-3" /> Next change available {lockedUntil?.toLocaleDateString()}</p>
        )}
      </div>
    );
  }

  // EDITING / CLAIMING flow
  return (
    <div className={`axia-card ${compact ? "p-4" : "p-6"} space-y-3`} data-testid="subdomain-claim-form">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-violet-600" />
        <div className="axia-overline">{b.subdomain ? "Change subdomain" : "Claim your branded URL"}</div>
      </div>
      <p className="text-sm text-slate-600">Pick a subdomain. Your workspace will live at <code className="font-mono text-slate-900">&lt;name&gt;.{root}</code> — perfect for kudos shares, webhook callbacks, and onboarding emails.</p>
      <div className="flex items-center gap-2">
        <div className="flex-1 flex items-center border border-slate-200 rounded-md focus-within:border-slate-400 bg-white">
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 30))}
            placeholder="acme"
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 font-mono text-sm"
            data-testid="subdomain-input"
            autoFocus={editing}
          />
          <span className="px-3 py-2 text-sm text-slate-500 font-mono select-none">.{root}</span>
        </div>
      </div>
      <div className="min-h-[20px] text-xs">
        {checking && <span className="text-slate-500 inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Checking…</span>}
        {!checking && check?.available && (
          <span className="text-emerald-700 inline-flex items-center gap-1.5" data-testid="subdomain-available"><Check className="w-3 h-3" /> {check.current ? "Current — no change" : `${value}.${root} is available.`}</span>
        )}
        {!checking && check && !check.available && (
          <span className="text-rose-700 inline-flex items-center gap-1.5" data-testid="subdomain-unavailable"><X className="w-3 h-3" /> {check.reason}</span>
        )}
      </div>
      <div className="flex items-center gap-2 justify-end">
        {editing && b.subdomain && (
          <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setValue(b.subdomain); setCheck(null); }} data-testid="subdomain-cancel">Cancel</Button>
        )}
        <Button onClick={claim} disabled={saving || !check?.available || check?.current} data-testid="subdomain-claim-btn">
          {saving && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />}
          {b.subdomain ? "Save" : "Claim subdomain"}
        </Button>
      </div>
      {!showLock && (
        <p className="text-[11px] text-slate-400">Heads up — changes are locked for 30 days after each claim.</p>
      )}
    </div>
  );
}

function CopyBtn({ text }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={async () => {
        await navigator.clipboard.writeText(text || "");
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      }}
      data-testid="subdomain-copy"
    >
      {done ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
    </Button>
  );
}
